/**
 * Availability Redux Slice
 */

import { createSlice, type PayloadAction } from '@spcc/core'
import type {
  FactorySummary,
  VariantSummary,
  AvailabilityStats,
  AvailabilityFilter,
  AvailabilityChange,
  AvailabilityRecord,
} from '../domain/types'
import { DEFAULT_AVAILABILITY_FILTER, DEFAULT_AVAILABILITY_STATS } from '../domain/constants'

// ============================================================================
// State Interface
// ============================================================================

export interface AvailabilityState {
  factories: FactorySummary[]
  variants: VariantSummary[]
  availability: Record<string, Record<string, boolean>> // factoryId -> variantId -> isAvailable
  originalAvailability: Record<string, Record<string, boolean>> // Original state for discard
  records: AvailabilityRecord[] // Existing records from API
  pendingChanges: AvailabilityChange[] // Changes to be saved
  stats: AvailabilityStats
  filter: AvailabilityFilter
  loading: boolean
  saving: boolean
  error: string | null
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: AvailabilityState = {
  factories: [],
  variants: [],
  availability: {},
  originalAvailability: {},
  records: [],
  pendingChanges: [],
  stats: DEFAULT_AVAILABILITY_STATS,
  filter: DEFAULT_AVAILABILITY_FILTER,
  loading: false,
  saving: false,
  error: null,
}

// ============================================================================
// Helper Functions
// ============================================================================

function calculateStats(
  factories: FactorySummary[],
  variants: VariantSummary[],
  availability: Record<string, Record<string, boolean>>
): AvailabilityStats {
  let totalAvailable = 0
  let totalUnavailable = 0

  factories.forEach((factory) => {
    variants.forEach((variant) => {
      const isAvailable = availability[factory.documentId]?.[variant.documentId] ?? true
      if (isAvailable) {
        totalAvailable++
      } else {
        totalUnavailable++
      }
    })
  })

  const totalCells = factories.length * variants.length

  return {
    totalFactories: factories.length,
    totalVariants: variants.length,
    totalAvailable,
    totalUnavailable,
    coveragePercentage: totalCells > 0 ? Math.round((totalAvailable / totalCells) * 100) : 0,
  }
}

function findPendingChangeIndex(
  pendingChanges: AvailabilityChange[],
  factoryDocumentId: string,
  variantDocumentId: string
): number {
  return pendingChanges.findIndex(
    (c) => c.factoryDocumentId === factoryDocumentId && c.variantDocumentId === variantDocumentId
  )
}

// ============================================================================
// Slice
// ============================================================================

export const availabilitySlice = createSlice({
  name: 'availability',
  initialState,
  reducers: {
    // ==========================================
    // Fetch matrix
    // ==========================================
    fetchMatrix: (state) => {
      state.loading = true
      state.error = null
    },

    fetchMatrixSuccess: (
      state,
      action: PayloadAction<{
        factories: FactorySummary[]
        variants: VariantSummary[]
        availability: Record<string, Record<string, boolean>>
        records: AvailabilityRecord[]
      }>
    ) => {
      state.factories = action.payload.factories
      state.variants = action.payload.variants
      state.availability = action.payload.availability
      state.originalAvailability = JSON.parse(JSON.stringify(action.payload.availability))
      state.records = action.payload.records
      state.pendingChanges = []
      state.loading = false
      state.stats = calculateStats(
        action.payload.factories,
        action.payload.variants,
        action.payload.availability
      )
    },

    fetchMatrixFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // ==========================================
    // Update availability (local state)
    // ==========================================
    updateAvailability: (
      state,
      action: PayloadAction<{ factoryId: string; variantId: string; isAvailable: boolean }>
    ) => {
      const { factoryId, variantId, isAvailable } = action.payload

      // Update local availability state
      if (!state.availability[factoryId]) {
        state.availability[factoryId] = {}
      }
      state.availability[factoryId][variantId] = isAvailable

      // Get original value
      const originalValue = state.originalAvailability[factoryId]?.[variantId] ?? true

      // Check if this change already exists in pending changes
      const existingIndex = findPendingChangeIndex(state.pendingChanges, factoryId, variantId)

      if (isAvailable === originalValue) {
        // Value is back to original, remove from pending changes
        if (existingIndex !== -1) {
          state.pendingChanges.splice(existingIndex, 1)
        }
      } else {
        // Value is different from original
        const change: AvailabilityChange = {
          factoryDocumentId: factoryId,
          variantDocumentId: variantId,
          isAvailable,
          originalValue,
        }

        if (existingIndex !== -1) {
          // Update existing change
          state.pendingChanges[existingIndex] = change
        } else {
          // Add new change
          state.pendingChanges.push(change)
        }
      }

      // Recalculate stats
      state.stats = calculateStats(state.factories, state.variants, state.availability)
    },

    // ==========================================
    // Save changes
    // ==========================================
    saveChanges: (state) => {
      state.saving = true
      state.error = null
    },

    saveChangesSuccess: (state) => {
      // Update original availability to match current
      state.originalAvailability = JSON.parse(JSON.stringify(state.availability))
      state.pendingChanges = []
      state.saving = false
    },

    saveChangesFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.saving = false
    },

    // ==========================================
    // Discard changes
    // ==========================================
    discardChanges: (state) => {
      // Revert to original availability
      state.availability = JSON.parse(JSON.stringify(state.originalAvailability))
      state.pendingChanges = []
      state.stats = calculateStats(state.factories, state.variants, state.availability)
    },

    // ==========================================
    // Filter
    // ==========================================
    setFilter: (state, action: PayloadAction<Partial<AvailabilityFilter>>) => {
      state.filter = { ...state.filter, ...action.payload }
    },

    // ==========================================
    // Error handling
    // ==========================================
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
      state.saving = false
    },

    clearError: (state) => {
      state.error = null
    },

    // ==========================================
    // Reset
    // ==========================================
    reset: () => initialState,
  },
})

export const availabilityActions = availabilitySlice.actions
export const availabilityReducer = availabilitySlice.reducer
